import { useCallback } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate, type NavigateOptions, type To } from 'react-router-dom'

/**
 * useNavigate with a view transition around the route change (F3): the content area slides, the rail stays put.
 * Same arguments as useNavigate. `back: true` (or a negative delta, or going Home) slides the other way.
 * Where startViewTransition is missing, or motion is reduced, it is a plain navigate, as before.
 * Needs <BrowserRouter useTransitions={false}> so the route state applies inside flushSync.
 */
export type NavOptions = NavigateOptions & { back?: boolean }
export function useNav() {
  const navigate = useNavigate()
  return useCallback((to: To | number, opts?: NavOptions) => {
    const back = !!opts?.back || (typeof to === 'number' && to < 0) || to === '/'
    const go = () => {
      if (typeof to === 'number') { navigate(to); return }
      const rest: NavigateOptions & { back?: boolean } = { ...(opts ?? {}) }
      delete rest.back
      navigate(to, rest)
    }
    const doc = document as Document & { startViewTransition?: (cb: () => void) => { ready?: Promise<void>; finished?: Promise<void> } | undefined }
    if (typeof doc.startViewTransition !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { go(); return }
    document.documentElement.dataset.nav = back ? 'back' : 'forward'
    try {
      const vt = doc.startViewTransition(() => flushSync(go))
      // a skipped transition (a second tap mid-slide, the app in the background) rejects these; that is fine and not an error
      vt?.ready?.catch(() => {}); vt?.finished?.catch(() => {})
    } catch { go() }
  }, [navigate])
}
